import { loadStripe, type Stripe, type StripeElements, type Appearance } from '@stripe/stripe-js'
import { appEnv } from 'config/app'
import { ref, type Ref } from 'vue'

let stripePromise: Promise<Stripe | null> | null = null

export type SetupIntentResult = { success: boolean; paymentMethodId?: string; error?: string }

/**
 * Get the Stripe instance (lazy-loaded singleton)
 * Returns null if no publishable key is configured
 */
export function getStripe(): Promise<Stripe | null> {
  if (!stripePromise) {
    const key = appEnv.stripePublishableKey
    if (!key) {
      console.warn('Stripe publishable key not configured (VITE_STRIPE_PUBLISHABLE_KEY)')
      stripePromise = Promise.resolve(null)
    } else {
      stripePromise = loadStripe(key)
    }
  }
  return stripePromise
}

/**
 * Default appearance for Stripe Elements (dark theme)
 */
export const stripeAppearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#00B0FF',
    colorBackground: '#0f1730',
    colorText: '#e2e8f0',
    colorDanger: '#ef4444',
    fontFamily: 'Inter, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px'
  },
  rules: {
    '.Input': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(120, 163, 255, 0.2)'
    },
    '.Input:focus': {
      border: '1px solid #00B0FF',
      boxShadow: '0 0 0 1px #00B0FF'
    },
    '.Label': {
      color: 'rgba(200, 218, 255, 0.7)'
    }
  }
}

/**
 * Light theme appearance for Stripe Elements
 */
export const stripeAppearanceLight: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#1c6ee3',
    colorBackground: '#ffffff',
    colorText: '#0f172a',
    colorDanger: '#ef4444',
    fontFamily: 'Inter, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px'
  }
}

/**
 * Composable for managing Stripe Elements
 */
export function useStripeElements(clientSecret: Ref<string | null>) {
  const stripe = ref<Stripe | null>(null)
  const elements = ref<StripeElements | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)

  const initialize = async (isDark = true) => {
    loading.value = true
    error.value = null

    try {
      const stripeInstance = await getStripe()
      if (!stripeInstance) {
        throw new Error('Stripe not configured')
      }

      stripe.value = stripeInstance

      if (clientSecret.value) {
        elements.value = stripeInstance.elements({
          clientSecret: clientSecret.value,
          appearance: isDark ? stripeAppearance : stripeAppearanceLight
        })
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to initialize Stripe'
    } finally {
      loading.value = false
    }
  }

  return {
    stripe,
    elements,
    loading,
    error,
    initialize
  }
}

/**
 * Confirm payment with Stripe Elements (PaymentElement)
 * Use this when collecting new card details
 */
export async function confirmPaymentWithElements(
  elements: StripeElements,
  returnUrl: string
): Promise<{ success: boolean; error?: string }> {
  const stripeInstance = await getStripe()

  if (!stripeInstance) {
    return { success: false, error: 'Stripe not configured' }
  }

  const { error } = await stripeInstance.confirmPayment({
    elements,
    confirmParams: {
      return_url: returnUrl
    }
  })

  // If we get here, there was an error (successful payments redirect immediately)
  if (error) {
    return {
      success: false,
      error: error.message || 'Payment confirmation failed'
    }
  }

  return { success: true }
}

/**
 * Confirm payment with a saved payment method
 * Use this when the customer has a payment method on file
 */
export async function confirmPaymentWithSavedMethod(
  clientSecret: string,
  paymentMethodId: string,
  returnUrl: string
): Promise<{ success: boolean; error?: string; requiresAction?: boolean }> {
  const stripe = await getStripe()

  if (!stripe) {
    return { success: false, error: 'Stripe not configured' }
  }

  const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
    payment_method: paymentMethodId,
    return_url: returnUrl
  })

  if (error) {
    return {
      success: false,
      error: error.message || 'Payment confirmation failed'
    }
  }

  if (paymentIntent?.status === 'requires_action') {
    // 3D Secure or other action required - Stripe will handle redirect
    return { success: false, requiresAction: true }
  }

  if (paymentIntent?.status === 'succeeded') {
    return { success: true }
  }

  // For other statuses, assume redirect is happening
  return { success: true }
}

/**
 * Confirm payment for 3DS when payment method is already attached
 * Use this when checkout status is 'requires_action' or 'requires_confirmation'
 */
export async function confirmCardPaymentFor3DS(
  clientSecret: string
): Promise<{ success: boolean; error?: string; needsPaymentMethod?: boolean }> {
  const stripe = await getStripe()
  if (!stripe) {
    return { success: false, error: 'Stripe not configured' }
  }

  const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret)

  if (error) {
    // If payment method is missing (e.g., after failed 3DS), signal to show payment form
    if (error.code === 'payment_intent_unexpected_state' ||
        error.message?.includes('payment method') ||
        error.message?.includes('payment_method')) {
      return { success: false, needsPaymentMethod: true }
    }
    return { success: false, error: error.message || '3DS authentication failed' }
  }

  if (paymentIntent?.status === 'succeeded' || paymentIntent?.status === 'processing') {
    return { success: true }
  }

  // If still requires payment method after confirmation attempt
  if (paymentIntent?.status === 'requires_payment_method') {
    return { success: false, needsPaymentMethod: true }
  }

  return { success: true }
}

/**
 * Confirm setup with Stripe Elements (PaymentElement)
 * Use this when saving a payment method for future use (SetupIntent)
 */
export async function confirmSetupWithElements(
  elements: StripeElements,
  returnUrl: string
): Promise<SetupIntentResult> {
  const stripeInstance = await getStripe()

  if (!stripeInstance) {
    return { success: false, error: 'Stripe not configured' }
  }

  const { error, setupIntent } = await stripeInstance.confirmSetup({
    elements,
    confirmParams: {
      return_url: returnUrl
    },
    redirect: 'if_required'
  })

  if (error) {
    return {
      success: false,
      error: error.message || 'Setup confirmation failed'
    }
  }

  if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
    const paymentMethodId = typeof setupIntent.payment_method === 'string'
      ? setupIntent.payment_method
      : setupIntent.payment_method.id
    return { success: true, paymentMethodId }
  }

  return { success: false, error: 'Setup did not complete' }
}

/**
 * Resolve a SetupIntent the customer was redirected back from.
 *
 * Redirect-based methods (PayPal being the one we offer) never resolve the
 * confirmSetup promise — the browser leaves the page for the provider and
 * returns to the return_url with the intent's client secret in the query. The
 * payment method therefore has to be picked up here on load instead.
 */
export async function retrieveSetupIntentResult(
  clientSecret: string
): Promise<SetupIntentResult> {
  const stripeInstance = await getStripe()

  if (!stripeInstance) {
    return { success: false, error: 'Stripe not configured' }
  }

  const { error, setupIntent } = await stripeInstance.retrieveSetupIntent(clientSecret)

  if (error) {
    return { success: false, error: error.message || 'Could not load setup result' }
  }

  if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
    const paymentMethodId =
      typeof setupIntent.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent.payment_method.id
    return { success: true, paymentMethodId }
  }

  // processing: the mandate is authorised but not yet usable. Treat it as a
  // non-error so the customer isn't shown a failure for a setup that is still
  // on its way to succeeding.
  if (setupIntent?.status === 'processing') {
    return { success: false }
  }

  return { success: false, error: 'Setup did not complete' }
}

/**
 * Legacy function - redirects based on client_secret
 * @deprecated Use confirmPaymentWithElements or confirmPaymentWithSavedMethod instead
 */
export async function confirmPayment(
  clientSecret: string,
  returnUrl: string
): Promise<{ success: boolean; error?: string }> {
  const stripe = await getStripe()

  if (!stripe) {
    return { success: false, error: 'Stripe not configured' }
  }

  // This only works if a payment method is already attached to the PaymentIntent
  // For new payments, use confirmPaymentWithElements instead
  const { error } = await stripe.confirmPayment({
    clientSecret,
    confirmParams: {
      return_url: returnUrl
    },
    redirect: 'if_required'
  })

  if (error) {
    return {
      success: false,
      error: error.message || 'Payment confirmation failed'
    }
  }

  return { success: true }
}
